import styled, { keyframes } from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import { HelpIcon } from "assets/icons/system";
import { ChartIcon } from "assets/icons/businessDashboard";

dayjs.locale("ko");

const SectionWrapper = styled.div`
  padding: 24px;
  background-color: #f3f7ff;
  position: relative;
`;

const BackColor = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 194px;
  background-color: #0f2859;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  color: #fff;
  position: relative;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.4px;
  font-weight: bold;
  color: #fff;
`;

const HelpButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: pointer;
  background: none;
  border: none;
  transition: color 0.2s;
  &:hover {
    color: #fff;
  }
`;

const SectionTime = styled.p`
  font-size: 12px;
  font-weight: bold;
  line-height: 14px;
  letter-spacing: -0.2px;
  color: #949db2;
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  position: relative;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonBox = styled.div<{
  width?: string;
  height?: string;
  radius?: string;
}>`
  width: ${({ width }) => width || "100%"};
  height: ${({ height }) => height || "20px"};
  border-radius: ${({ radius }) => radius || "4px"};
  background: #e0e0e0;
  background-image: linear-gradient(
    90deg,
    #e0e0e0 0px,
    #f5f5f5 40px,
    #e0e0e0 80px
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.2s infinite linear;
`;

const Article = styled.article`
  overflow: hidden;
  border-top-left-radius: 28px;
  border-top-right-radius: 28px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  background: none;
`;

const CardBg = styled.div`
  color: #fff;
  position: relative;
  background: #e3e7ed;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 60px 16px 0 16px;
`;

const CardHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ChartIconRow = styled.div`
  height: 30px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-bottom: 12px;
  padding-right: 28px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  height: 38px;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: -0.2px;
  background: linear-gradient(90deg, #e3e7ed 0%, #f0f2f5 100%);
`;

const CardBody = styled.div`
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  width: 100%;
  padding: 20px 12px;
  background-color: white;
`;

const CardBodyTitle = styled.p`
  font-size: 12px;
  font-weight: 700;
  color: #6c727f;
  line-height: 14px;
  letter-spacing: -0.2px;
  padding-left: 1rem;
  margin-bottom: 12px;
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BreakdownItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  justify-content: space-between;
  border-radius: 8px;
  padding-left: 13.95px;
  padding-right: 13.95px;
`;

const BreakdownItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0 6px;
`;

const BreakdownItemDot = styled.div`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background: #e0e0e0;
`;

const BreakdownItemRight = styled.div`
  text-align: right;
`;

export const RealtimeSalesSectionSkeleton = () => {
  return (
    <SectionWrapper>
      <BackColor />
      <SectionHeader>
        <ChartIcon />
        <HeaderContent>
          <SectionTitle>실시간 매출</SectionTitle>
          <HelpButton type="button">
            <HelpIcon />
          </HelpButton>
          <SectionTime>오늘 {dayjs().format("HH:mm")} 기준</SectionTime>
        </HeaderContent>
      </SectionHeader>
      <CardsGrid>
        <RevenueCardSkeleton />
      </CardsGrid>
    </SectionWrapper>
  );
};

const RevenueCardSkeleton = () => (
  <Article>
    <CardBg>
      <CardHeader>
        <CardHeaderRow>
          <SkeletonBox width="80px" height="18px" />
        </CardHeaderRow>
        <SkeletonBox
          width="180px"
          height="36px"
          radius="10px"
          style={{ marginTop: "12px" }}
        />
      </CardHeader>
      <ChartIconRow>
        <SkeletonBox width="30px" height="30px" radius="50%" />
      </ChartIconRow>
      <CardFooter>
        <SkeletonBox width="60px" height="14px" />
        <SkeletonBox width="80px" height="14px" />
      </CardFooter>
    </CardBg>
    <CardBody>
      <CardBodyTitle>
        <SkeletonBox width="70px" height="14px" />
      </CardBodyTitle>
      <BreakdownList>
        {[...Array(5)].map((_, idx) => (
          <BreakdownItemRow key={idx}>
            <BreakdownItemInfo>
              <BreakdownItemDot />
              <SkeletonBox width="50px" height="20px" />
            </BreakdownItemInfo>
            <BreakdownItemRight>
              <SkeletonBox width="60px" height="20px" />
              <SkeletonBox
                width="40px"
                height="14px"
                style={{ marginTop: "4px" }}
              />
            </BreakdownItemRight>
          </BreakdownItemRow>
        ))}
      </BreakdownList>
    </CardBody>
  </Article>
);
